import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  IconAlertCircle, IconCheck, IconLock, IconChevronLeft, IconHistory,
} from '@tabler/icons-react';
import AdminLayout from '../../components/layout/AdminLayout';
import Modal from '../../components/ui/Modal';
import { useManagerApproval } from '../../hooks/useManagerApproval';

/**
 * Phase 4.7 — Closed-day detail + corrections panel.
 *
 * Admin sees the full Z-Report and can edit openingFloatCents,
 * closingFloatCents, countedCashCents, closeNotes. Expected cash +
 * variance recompute LIVE as they type. Save flow: review diff →
 * mandatory reason → manager-PIN → audit + CORRECTED Z-Report email.
 *
 * Locked once a later day's session has closed.
 */

interface Session {
  id: string;
  openedAt: string;
  closedAt: string | null;
  openingFloatCents: number;
  closingFloatCents: number | null;
  countedCashCents: number | null;
  expectedCashCents: number | null;
  varianceCents: number | null;
  closeNotes: string | null;
  openedBy: { name: string } | null;
  closedBy: { name: string } | null;
}

interface Report {
  session: Session;
  totals: {
    grossSalesCents: number;
    netSalesCents: number;
    vatCents: number;
    discountCents: number;
    refundedCents: number;
    tipCents: number;
    cancelledCents: number;
    voidedCents: number;
    counts: Record<string, number>;
  };
  paymentsByMethod: { method: string; count: number; amountCents: number; netCents: number; refundedAmountCents: number }[];
  cash: {
    openingFloatCents: number;
    cashSalesNetCents: number;
    paidInCents: number;
    paidOutCents: number;
    expectedCashCents: number;
  };
  highlights: {
    orderCount: number;
    customerCount: number;
    busiestHourLabel: string | null;
    topProducts: { name: string; qtySold: number }[];
    topCategories: { name: string; orderCount: number }[];
  };
}

interface ReportEnvelope {
  report: Report;
  lockedByNextDay: boolean;
}

interface CorrectionEvent {
  id: string;
  ts: string;
  reason: string | null;
  actor: { name: string } | null;
  before: Record<string, number | string | null>;
  after: Record<string, number | string | null>;
}

function fmtR(cents: number | null | undefined) {
  if (cents === null || cents === undefined) return '—';
  return `R ${(cents / 100).toFixed(2)}`;
}

function methodLabel(m: string) {
  return m === 'CARD_YOCO' ? 'Card' : m === 'CASH' ? 'Cash' : m === 'EFT' ? 'EFT' :
         m === 'ACCOUNT' ? 'Account' : m === 'VOUCHER' ? 'Voucher' :
         m === 'GIFT_CARD' ? 'Gift card' : 'Other';
}

function fmtDateTime(iso: string) {
  return new Date(iso).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Africa/Johannesburg' });
}

// ===========================================================================

export default function AdminDayDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const askApproval = useManagerApproval();
  const [envelope, setEnvelope] = useState<ReportEnvelope | null>(null);
  const [corrections, setCorrections] = useState<CorrectionEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Draft values (rands as strings for natural input)
  const [draftOpeningRand, setDraftOpeningRand] = useState('');
  const [draftClosingRand, setDraftClosingRand] = useState('');
  const [draftCountedRand, setDraftCountedRand] = useState('');
  const [draftNotes, setDraftNotes] = useState('');

  const [reviewOpen, setReviewOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [zr, cr] = await Promise.all([
        fetch(`/api/day-sessions/${id}/z-report`, { credentials: 'include' }),
        fetch(`/api/day-sessions/${id}/corrections`, { credentials: 'include' }),
      ]);
      if (!zr.ok) throw new Error(`HTTP ${zr.status}`);
      const env = (await zr.json()) as ReportEnvelope;
      setEnvelope(env);
      setDraftOpeningRand((env.report.session.openingFloatCents / 100).toFixed(2));
      setDraftClosingRand((env.report.session.closingFloatCents ?? 0) === 0 ? '' : ((env.report.session.closingFloatCents ?? 0) / 100).toFixed(2));
      setDraftCountedRand((env.report.session.countedCashCents ?? 0) === 0 ? '' : ((env.report.session.countedCashCents ?? 0) / 100).toFixed(2));
      setDraftNotes(env.report.session.closeNotes ?? '');
      if (cr.ok) {
        const data = await cr.json();
        setCorrections(data.corrections ?? []);
      }
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [id]);

  // ---------------------------------------------------------------------------
  // Live recalculation
  // ---------------------------------------------------------------------------

  const draftOpeningCents = Math.round(Number(draftOpeningRand.replace(',', '.')) * 100);
  const draftClosingCents = draftClosingRand.trim() === '' ? null : Math.round(Number(draftClosingRand.replace(',', '.')) * 100);
  const draftCountedCents = draftCountedRand.trim() === '' ? null : Math.round(Number(draftCountedRand.replace(',', '.')) * 100);

  const liveExpected = useMemo(() => {
    if (!envelope) return 0;
    return draftOpeningCents
      + envelope.report.cash.cashSalesNetCents
      + envelope.report.cash.paidInCents
      - envelope.report.cash.paidOutCents;
  }, [envelope, draftOpeningCents]);

  const liveVariance = useMemo(() => {
    if (draftCountedCents === null) return null;
    return draftCountedCents - liveExpected;
  }, [draftCountedCents, liveExpected]);

  const original = envelope?.report.session;
  const dirty = original ? (
    draftOpeningCents !== original.openingFloatCents
    || (draftClosingCents ?? 0) !== (original.closingFloatCents ?? 0)
    || (draftCountedCents ?? 0) !== (original.countedCashCents ?? 0)
    || draftNotes.trim() !== (original.closeNotes ?? '')
  ) : false;

  const changedFields = useMemo(() => {
    if (!original) return [] as { label: string; was: string; now: string }[];
    const out: { label: string; was: string; now: string }[] = [];
    if (draftOpeningCents !== original.openingFloatCents) {
      out.push({ label: 'Opening float', was: fmtR(original.openingFloatCents), now: fmtR(draftOpeningCents) });
    }
    if ((draftClosingCents ?? 0) !== (original.closingFloatCents ?? 0)) {
      out.push({ label: 'Closing float', was: fmtR(original.closingFloatCents), now: fmtR(draftClosingCents ?? 0) });
    }
    if ((draftCountedCents ?? 0) !== (original.countedCashCents ?? 0)) {
      out.push({ label: 'Counted cash', was: fmtR(original.countedCashCents), now: fmtR(draftCountedCents ?? 0) });
    }
    if (draftNotes.trim() !== (original.closeNotes ?? '')) {
      out.push({ label: 'Notes', was: original.closeNotes ?? '(none)', now: draftNotes.trim() || '(cleared)' });
    }
    // Always show recomputed variance if anything changed.
    if (out.length > 0 && draftCountedCents !== null) {
      out.push({ label: 'Variance (recomputed)', was: fmtR(original.varianceCents), now: fmtR(liveVariance) });
    }
    return out;
  }, [draftOpeningCents, draftClosingCents, draftCountedCents, draftNotes, liveVariance, original]);

  // ---------------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------------

  async function save() {
    if (!reason.trim() || saving || !envelope) return;
    // Manager-PIN step-up. Server requires elevation too — defence in depth.
    const ok = await askApproval({
      action: 'Save day-session correction',
      description: `${changedFields.length} field${changedFields.length === 1 ? '' : 's'} changed. Manager approval required.`,
    });
    if (!ok) return;
    setSaving(true);
    try {
      const body: Record<string, unknown> = { reason: reason.trim() };
      if (draftOpeningCents !== original?.openingFloatCents) body.openingFloatCents = draftOpeningCents;
      if ((draftClosingCents ?? 0) !== (original?.closingFloatCents ?? 0)) body.closingFloatCents = draftClosingCents ?? 0;
      if ((draftCountedCents ?? 0) !== (original?.countedCashCents ?? 0)) body.countedCashCents = draftCountedCents ?? 0;
      if (draftNotes.trim() !== (original?.closeNotes ?? '')) body.closeNotes = draftNotes.trim() || null;

      const r = await fetch(`/api/day-sessions/${id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.error ?? `HTTP ${r.status}`);
      }
      setReviewOpen(false);
      setReason('');
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  // ---------------------------------------------------------------------------

  if (loading || !envelope) {
    return <AdminLayout title="Day session"><p className="text-13 italic text-gray-700">Loading…</p></AdminLayout>;
  }

  const isOpen = !envelope.report.session.closedAt;
  const locked = envelope.lockedByNextDay;
  const canEdit = !isOpen && !locked;

  return (
    <AdminLayout
      title={`Day · ${new Date(envelope.report.session.openedAt).toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Africa/Johannesburg' })}`}
      subtitle={isOpen ? 'Open session' : `Closed by ${envelope.report.session.closedBy?.name ?? '—'}`}
      actions={
        <button
          type="button"
          onClick={() => navigate('/admin/days')}
          className="h-10 px-4 inline-flex items-center gap-2 rounded-md bg-light border border-border text-13 font-bold text-heading hover:bg-gray-100"
        >
          <IconChevronLeft size={14} stroke={2.5} />
          All days
        </button>
      }
    >
      {error && (
        <div className="rounded-md bg-danger-soft border border-danger/30 p-3 flex items-start gap-2 mb-5">
          <IconAlertCircle size={18} className="text-danger flex-shrink-0 mt-0.5" />
          <p className="text-13 font-semibold text-danger">{error}</p>
        </div>
      )}

      {locked && (
        <div className="rounded-md bg-warning-soft border border-warning/30 p-3 flex items-start gap-2 mb-5">
          <IconLock size={18} className="text-warning flex-shrink-0 mt-0.5" />
          <p className="text-13 font-semibold text-warning">
            A later day has already been closed — this session is locked. Amounts are read-only.
          </p>
        </div>
      )}

      {/* Sales summary (read-only) */}
      <section className="rounded-lg bg-surface border border-border p-5 mb-5">
        <h2 className="text-13 font-bold uppercase tracking-wider text-gray-700 mb-3">Sales</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Tile label="Net sales" value={fmtR(envelope.report.totals.netSalesCents)} bold />
          <Tile label="Gross sales" value={fmtR(envelope.report.totals.grossSalesCents)} />
          <Tile label="VAT (incl.)" value={fmtR(envelope.report.totals.vatCents)} />
          <Tile label="Tips" value={fmtR(envelope.report.totals.tipCents)} />
          <Tile label="Discounts" value={fmtR(envelope.report.totals.discountCents)} />
          <Tile label="Refunded" value={fmtR(envelope.report.totals.refundedCents)} />
          <Tile label="Cancelled" value={fmtR(envelope.report.totals.cancelledCents)} />
          <Tile label="Voided" value={fmtR(envelope.report.totals.voidedCents)} />
        </div>
      </section>

      {/* Payments by method (read-only) */}
      <section className="rounded-lg bg-surface border border-border p-5 mb-5">
        <h2 className="text-13 font-bold uppercase tracking-wider text-gray-700 mb-3">By payment method</h2>
        {envelope.report.paymentsByMethod.length === 0 ? (
          <p className="text-13 italic text-gray-700">No payments.</p>
        ) : (
          <ul className="space-y-2">
            {envelope.report.paymentsByMethod.map((p) => (
              <li key={p.method} className="flex items-center justify-between p-3 rounded-md bg-light border border-border-soft">
                <div>
                  <p className="text-14 font-bold text-heading">{methodLabel(p.method)}</p>
                  <p className="text-12 font-medium text-gray-700">{p.count} payment{p.count === 1 ? '' : 's'}{p.refundedAmountCents > 0 ? ` · refunds ${fmtR(p.refundedAmountCents)}` : ''}</p>
                </div>
                <p className="text-15 font-bold text-heading tabular-nums">{fmtR(p.netCents)}</p>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Cash drawer + EDITABLE cash fields */}
      <section className="rounded-lg bg-surface border border-border p-5 mb-5">
        <h2 className="text-13 font-bold uppercase tracking-wider text-gray-700 mb-3">Cash drawer</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <EditableCents
            label="Opening float"
            value={draftOpeningRand}
            onChange={setDraftOpeningRand}
            original={original?.openingFloatCents ?? 0}
            disabled={!canEdit}
          />
          <ReadTile label="+ Cash sales (net)" value={fmtR(envelope.report.cash.cashSalesNetCents)} />
          <ReadTile label="+ Paid in" value={fmtR(envelope.report.cash.paidInCents)} />
          <ReadTile label="− Paid out" value={fmtR(envelope.report.cash.paidOutCents)} />
          <ReadTile
            label="Expected (recomputed live)"
            value={fmtR(liveExpected)}
            tone={liveExpected !== envelope.report.cash.expectedCashCents ? 'primary' : 'default'}
            sub={liveExpected !== envelope.report.cash.expectedCashCents ? `was ${fmtR(envelope.report.cash.expectedCashCents)}` : undefined}
          />
          <EditableCents
            label="Counted cash"
            value={draftCountedRand}
            onChange={setDraftCountedRand}
            original={original?.countedCashCents ?? 0}
            disabled={!canEdit}
          />
          <EditableCents
            label="Closing float (left in till)"
            value={draftClosingRand}
            onChange={setDraftClosingRand}
            original={original?.closingFloatCents ?? 0}
            disabled={!canEdit}
          />
          <ReadTile
            label="Variance (recomputed live)"
            value={liveVariance === null ? '—' : (liveVariance > 0 ? '+' : '') + fmtR(liveVariance)}
            tone={liveVariance === null ? 'default' : liveVariance === 0 ? 'success' : Math.abs(liveVariance) > 20000 ? 'danger' : 'warning'}
            sub={original?.varianceCents !== null && original?.varianceCents !== liveVariance ? `was ${(original?.varianceCents ?? 0) > 0 ? '+' : ''}${fmtR(original?.varianceCents)}` : undefined}
          />
        </div>

        <label className="block">
          <span className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">Close notes</span>
          <textarea
            value={draftNotes}
            onChange={(e) => setDraftNotes(e.target.value)}
            disabled={!canEdit}
            rows={2}
            placeholder="Anything worth recording about the close"
            className="w-full px-3 py-2 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-medium text-heading resize-none disabled:opacity-60"
          />
        </label>
      </section>

      {/* Corrections history */}
      {corrections.length > 0 && (
        <section className="rounded-lg bg-surface border border-border p-5 mb-5">
          <h2 className="text-13 font-bold uppercase tracking-wider text-gray-700 mb-3 inline-flex items-center gap-2">
            <IconHistory size={14} stroke={2.25} />
            Corrections history
          </h2>
          <ul className="space-y-2">
            {corrections.map((c) => (
              <li key={c.id} className="p-3 rounded-md bg-light border border-border-soft text-13">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-bold text-heading">{c.actor?.name ?? '—'}</span>
                  <span className="text-12 font-medium text-gray-700 tabular-nums">{fmtDateTime(c.ts)}</span>
                </div>
                {c.reason && <p className="italic text-gray-700 mb-1">"{c.reason}"</p>}
                <CorrectionDiff before={c.before} after={c.after} />
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Save bar */}
      {canEdit && dirty && (
        <div className="sticky bottom-0 -mx-5 px-5 py-3 bg-surface border-t-2 border-primary flex items-center justify-between shadow-[0_-8px_24px_-8px_rgba(0,0,0,0.15)]">
          <p className="text-13 font-bold text-heading">
            {changedFields.length - (changedFields.some((f) => f.label.includes('Variance')) ? 1 : 0)} field{changedFields.length === 1 ? '' : 's'} changed — review before saving.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => { void load(); }}
              className="h-10 px-4 rounded-md bg-light border border-border text-13 font-bold text-heading hover:bg-gray-100"
            >
              Discard
            </button>
            <button
              type="button"
              onClick={() => setReviewOpen(true)}
              className="h-10 px-4 inline-flex items-center gap-2 rounded-md bg-primary text-[#FFFFFF] text-13 font-bold hover:bg-primary-hover shadow-sm"
            >
              <IconCheck size={14} stroke={2.5} />
              Review corrections
            </button>
          </div>
        </div>
      )}

      {/* Review modal */}
      <Modal open={reviewOpen} onClose={() => setReviewOpen(false)} size="md" title="Review corrections">
        <div className="space-y-3 mb-4">
          {changedFields.map((c, i) => (
            <div key={i} className="p-3 rounded-md bg-light border border-border-soft">
              <p className="text-12 font-bold uppercase tracking-wider text-gray-700 mb-1">{c.label}</p>
              <div className="flex items-center gap-2 text-14">
                <span className="text-gray-700 line-through">{c.was}</span>
                <span>→</span>
                <span className="font-bold text-heading">{c.now}</span>
              </div>
            </div>
          ))}
        </div>

        <label className="block mb-4">
          <span className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">
            Reason <span className="text-danger">*</span>
            <span className="ml-2 normal-case tracking-normal font-medium text-gray-600">— why are we correcting this?</span>
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="e.g. Re-count next morning found R50 extra — typo on counted cash."
            className="w-full px-3 py-2 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-14 font-medium text-heading resize-none"
          />
        </label>

        <p className="text-12 font-medium text-gray-700 mb-4">
          Saving will require manager PIN, write an audit record, and re-send the CORRECTED Z-Report email to the business address.
        </p>

        <div className="flex items-stretch gap-2">
          <button onClick={() => setReviewOpen(false)} disabled={saving} className="flex-1 h-12 rounded-md bg-light border border-border text-14 font-bold text-heading hover:bg-gray-100">Cancel</button>
          <button
            onClick={save}
            disabled={saving || !reason.trim()}
            className="flex-[2] h-12 inline-flex items-center justify-center gap-2 rounded-md bg-primary text-[#FFFFFF] text-14 font-bold hover:bg-primary-hover disabled:bg-gray-300"
          >
            <IconCheck size={16} stroke={2.5} />
            {saving ? 'Saving…' : 'Confirm + manager PIN'}
          </button>
        </div>
      </Modal>
    </AdminLayout>
  );
}

// ---------------------------------------------------------------------------

function Tile({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="rounded-md bg-light border border-border-soft p-3">
      <p className="text-11 font-bold uppercase tracking-wider text-gray-700">{label}</p>
      <p className={`tabular-nums ${bold ? 'text-18 font-bold text-heading' : 'text-15 font-semibold text-heading'} mt-0.5`}>{value}</p>
    </div>
  );
}

function ReadTile({ label, value, sub, tone = 'default' }: { label: string; value: string; sub?: string; tone?: 'default' | 'primary' | 'success' | 'warning' | 'danger' }) {
  const toneCls = {
    default: 'bg-light border-border-soft text-heading',
    primary: 'bg-primary-soft border-primary/30 text-primary',
    success: 'bg-success-soft border-success/30 text-success',
    warning: 'bg-warning-soft border-warning/30 text-warning',
    danger:  'bg-danger-soft border-danger/30 text-danger',
  }[tone];
  return (
    <div className={`rounded-md border p-3 ${toneCls}`}>
      <p className="text-11 font-bold uppercase tracking-wider opacity-80">{label}</p>
      <p className="text-16 font-bold tabular-nums mt-0.5">{value}</p>
      {sub && <p className="text-11 font-medium opacity-70 mt-0.5">{sub}</p>}
    </div>
  );
}

function EditableCents({
  label, value, onChange, original, disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  original: number;
  disabled?: boolean;
}) {
  const liveCents = Math.round(Number((value || '0').replace(',', '.')) * 100);
  const changed = !disabled && (value.trim() !== '' || original !== 0) && liveCents !== original;
  return (
    <div className={`rounded-md border-2 p-3 ${changed ? 'border-primary bg-primary-soft' : 'border-border-soft bg-light'}`}>
      <p className="text-11 font-bold uppercase tracking-wider text-gray-700">{label}</p>
      <div className="flex items-baseline gap-2 mt-1">
        <span className="text-13 font-bold text-gray-700">R</span>
        <input
          type="text"
          inputMode="decimal"
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/[^0-9.,]/g, ''))}
          disabled={disabled}
          placeholder="0.00"
          className="flex-1 h-9 px-2 rounded-md bg-surface border border-border focus:border-primary outline-none text-16 font-bold tabular-nums text-heading disabled:opacity-60"
        />
      </div>
      {changed && (
        <p className="text-11 font-medium text-primary mt-1 tabular-nums">
          was R {(original / 100).toFixed(2)}
        </p>
      )}
    </div>
  );
}

function CorrectionDiff({ before, after }: { before: Record<string, number | string | null>; after: Record<string, number | string | null> }) {
  const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]));
  return (
    <ul className="space-y-0.5 mt-1">
      {keys.map((k) => {
        const b = before[k];
        const a = after[k];
        if (b === a) return null;
        const fmt = (v: number | string | null | undefined) => {
          if (v === null || v === undefined) return '—';
          if (typeof v === 'number' && k.toLowerCase().includes('cents')) return fmtR(v);
          return String(v);
        };
        const niceLabel = k
          .replace(/Cents$/, '')
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, (s) => s.toUpperCase())
          .trim();
        return (
          <li key={k} className="text-12 text-heading">
            <span className="font-bold">{niceLabel}:</span>{' '}
            <span className="text-gray-700 line-through">{fmt(b)}</span>{' '}
            →{' '}
            <span className="font-bold">{fmt(a)}</span>
          </li>
        );
      })}
    </ul>
  );
}
